'use client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAppSelector } from '@/lib/redux/hooks';
import { selectPlans } from '@/lib/redux/selectors/pricingSelectors';
import type { PlanTag } from '@/types/plan-catalog.generated';
import type { ServerPlan } from '@/types/pricing';
import { ArrowRight, Building2, Users } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { useId, useMemo, useState } from 'react';
import { buildPlanFromServerPlan } from './PricingV2Section';
import { vatAdjustedDisplayPrice } from './pricing-helpers';
import {
  seatContactThreshold,
  seatDiscountBands,
  seatDiscountPct,
  seatUnitPriceEur,
  shouldWithholdSeatPrice,
} from './seatPricing';

/**
 * Firms & Teams pricing — the published per-seat ladder.
 *
 * Firms buy the SAME individual plans as everyone else, by the seat, with a
 * volume discount. There is no separate org plan ladder any more: `starter_org`
 * / `plus_org` were retired in
 * `20260908045256_publish_firm_seat_pricing.sql` because they cost more than
 * the individual tiers AND silently dropped their capabilities, which is why
 * prod carried one organization and zero org subscriptions. Every seat now
 * carries the full individual-tier capability set (Profil+ listing included)
 * and there is no admin fee.
 *
 * Everything numeric on this surface is DB-derived, nothing is copy:
 *   - tier NAMES and taglines come from `plan_translations` via the catalog
 *     (`buildPlanFromServerPlan`), never from the message bundle — the section
 *     previously hardcoded "Starter Org" / "Plus Org" in i18n, which is how it
 *     kept saying "Plus" months after the catalog renamed the tier to "Pro";
 *   - per-seat prices, band boundaries, the discount percentages and the
 *     contact-sales threshold are all derived from `plans.price_tiers`
 *     (see ./seatPricing). The discount line was previously hardcoded as
 *     "10+ miest: 11-17% zľava" and was already wrong for the prices it
 *     described.
 *
 * The ladder's open-ended (top) band is stored but deliberately NOT published:
 * at or above `seatContactThreshold` the price display is replaced by the
 * contact CTA, so a negotiated deal has a documented floor without advertising
 * one.
 */

/** Tiers shown on this tab, cheapest first. Tags, never display names. */
const FIRM_PLAN_TAGS: readonly PlanTag[] = ['starter', 'plus', 'kancelaria'];

interface OrgPricingSectionProps {
  /**
   * VAT-display selection from the shared VatControls. Firms are always a
   * business, so ``isBusiness`` is implied true — only the VAT-payer flag and
   * country vary. Seat prices are SK-gross; defaults reproduce the plain SK
   * consumer view (non-payer SK → unchanged).
   */
  country?: string;
  isVatPayer?: boolean;
}

/**
 * Parse the seat input into a usable count.
 *
 * The field is free text while the user edits it (an empty box mid-retype must
 * not snap back to 1), so a blank or malformed value resolves to the minimum
 * rather than to `NaN`.
 */
function parseSeats(raw: string): number {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return parsed;
}

interface SeatTierCardProps {
  plan: ServerPlan;
  seats: number;
  /**
   * Whether this seat count is at or above the threshold where the per-seat
   * price is withheld in favour of a contact CTA.
   *
   * Passed in, deliberately, rather than re-derived here from
   * ``contactThreshold`` + ``seats``. The card used to compute it with an
   * expression identical to the parent's, and "the 25+ band is stored but must
   * never be published" is the whole point of this section — so having the rule
   * live in two places meant a regression could break one copy while the other
   * kept the source-text fence green. One owner, one expression.
   */
  isContactSales: boolean;
  country: string;
  isVatPayer: boolean;
  locale: string;
  t: ReturnType<typeof useTranslations<'PricingPage'>>;
}

/**
 * Exported for testing: this card takes ``t`` as a PROP rather than calling
 * ``useTranslations`` itself, so it renders under plain
 * ``@testing-library/react`` with a stub ``t`` and needs no next-intl context
 * — the same shape that lets ``PricingV2Section.test.tsx`` render ``PlanCard``
 * directly. That matters here because the repo's established fallback for
 * intl-consuming components is a source-text scan (see
 * ``SidebarMenuContent.test.tsx``: process-global ``mock.module`` calls leak
 * across sibling files in bun), and a source scan cannot tell "the withhold
 * rule fired" from "the string still appears in the file".
 */
export function SeatTierCard({
  plan,
  seats,
  isContactSales,
  country,
  isVatPayer,
  locale,
  t,
}: SeatTierCardProps) {
  // Catalog resolution (display name, tagline) — shared with the Individuals
  // tab so both tabs cannot disagree about what a tier is called.
  const catalogPlan = buildPlanFromServerPlan(plan, t, locale);

  const unitGross = seatUnitPriceEur(plan, seats);
  const discountPct = seatDiscountPct(plan.price_tiers, seats);

  // Seat prices are SK-gross; adjust to the customer's VAT view (gross for a
  // non-payer, net + DPH for a domestic VAT-payer, 0 % for reverse-charge).
  const vatOpts = {
    country,
    isBusiness: true,
    isVatPayer,
    locale,
  } as const;

  const unit =
    unitGross === null ? null : vatAdjustedDisplayPrice(unitGross, vatOpts);

  // The TOTAL is the DISPLAYED unit times the seat count — deliberately, and
  // this was re-litigated against live data, so do not "fix" it again.
  //
  // Rounding the whole line once instead (`vatAdjustedDisplayPrice(unitGross *
  // seats)`) is arithmetically purer and was tried: it made the DEFAULT view
  // worse. `vatAdjustedDisplayPrice` strips SK VAT to net and re-applies the
  // customer's rate, and that round-trip is not identity at 2 dp. Ladder prices
  // are whole euros, so `unit x seats` is EXACT in the gross view — while
  // rounding the line once rendered, against prod data: 2 seats 37.99/97.99/
  // 198.01, 5 seats 85.01, 24 seats 384.01 and 2015.99. A visitor reading
  // "17.00 EUR/miesto/mes" beside "Spolu 85.01" sees arithmetic that does not
  // add up, in the view most of them get.
  //
  // The cost of this choice is confined to the VAT-payer NET view, where the
  // unit is not a whole number: at 9 seats it reads 124.38/321.93/651.24 where
  // exact would be 124.39/321.95/651.22 — at most 2 cents, on a marketing page
  // that quotes no binding amount (Stripe bills from the gross tiered price).
  // Self-consistency in the common view beats 2 cents in the rare one.
  const total =
    unit === null ? null : { displayPrice: unit.displayPrice * seats };

  const withhold = shouldWithholdSeatPrice({
    hasLadder: (plan.price_tiers?.length ?? 0) > 0,
    seats,
    isContactSales,
    hasUnitPrice: unit !== null,
  });

  return (
    <div className='rounded-lg border border-line-subtle bg-surface-card-fancy p-5 flex flex-col'>
      <h3 className='text-base font-semibold text-content-primary'>
        {catalogPlan.name}
      </h3>
      {catalogPlan.description && (
        <p className='mt-1 text-xs text-content-muted'>
          {catalogPlan.description}
        </p>
      )}

      {withhold || unit === null || total === null ? null : (
        <>
          <p className='mt-4 text-sm font-medium text-content-primary'>
            {t('orgPerSeat', { price: unit.displayPrice.toFixed(2) })}
          </p>
          <p className='mt-1 text-sm text-content-secondary'>
            {t('orgMonthlyTotal', {
              price: total.displayPrice.toFixed(2),
            })}
          </p>
          {discountPct !== null && discountPct > 0 && (
            <p className='mt-2 text-xs font-medium text-status-success-text'>
              {t('orgSeatSaving', { pct: discountPct })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

export function OrgPricingSection({
  country = 'SK',
  isVatPayer = false,
}: OrgPricingSectionProps = {}) {
  const t = useTranslations('PricingPage');
  const locale = useLocale();
  const plans = useAppSelector(selectPlans);
  const seatsInputId = useId();

  const [seatsInput, setSeatsInput] = useState('5');
  const seats = parseSeats(seatsInput);

  // Cheapest-first, and only the tiers actually present in the catalog — a
  // cold load before PricingInitializer dispatches renders no cards rather
  // than empty placeholders.
  const firmPlans = useMemo(
    () =>
      FIRM_PLAN_TAGS.map((tag) => plans.find((p) => p.id === tag)).filter(
        (plan): plan is ServerPlan => plan !== undefined
      ),
    [plans]
  );

  // Section-level threshold: the lowest seat count at which ANY shown tier
  // stops publishing a price. Derived, so a re-banding migration moves it.
  const contactThreshold = useMemo(() => {
    const thresholds = firmPlans
      .map((plan) => seatContactThreshold(plan.price_tiers))
      .filter((value): value is number => value !== null);
    return thresholds.length > 0 ? Math.min(...thresholds) : null;
  }, [firmPlans]);

  const discountBands = useMemo(
    () => seatDiscountBands(firmPlans),
    [firmPlans]
  );

  const isContactSales = contactThreshold !== null && seats >= contactThreshold;

  // Every tier shares the same VAT view, so one suffix line covers the grid.
  const vatSuffix = vatAdjustedDisplayPrice(1, {
    country,
    isBusiness: true,
    isVatPayer,
    locale,
  }).vatSuffix;

  return (
    <section
      aria-labelledby='org-pricing-heading'
      className='w-full max-w-3xl mx-auto'
    >
      <div className='rounded-xl border border-line-default border-t-2 border-t-brand-tertiary/30 bg-surface-card p-8 md:p-10'>
        {/* Header */}
        <div className='flex items-center gap-3 mb-3'>
          <Building2
            className='h-6 w-6 text-brand-tertiary flex-shrink-0'
            aria-hidden='true'
          />
          <h2
            id='org-pricing-heading'
            className='text-xl font-semibold text-content-primary'
          >
            {t('orgSectionTitle')}
          </h2>
        </div>

        <p className='text-sm text-content-secondary mb-8'>
          {t('orgSectionSubtitle')}
        </p>

        {/* Seat count — drives every price below. */}
        <div className='mb-8'>
          <Label
            htmlFor={seatsInputId}
            className='block mb-2 text-content-primary'
          >
            {t('orgSeatsLabel')}
          </Label>
          <Input
            id={seatsInputId}
            type='number'
            inputMode='numeric'
            min={1}
            step={1}
            value={seatsInput}
            onChange={(event) => setSeatsInput(event.target.value)}
            className='min-h-[44px] w-32 text-content-primary'
          />
        </div>

        {/* Plan cards — names and taglines resolved from the plan catalog. */}
        {firmPlans.length > 0 && (
          <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8'>
            {firmPlans.map((plan) => (
              <SeatTierCard
                key={plan.id}
                plan={plan}
                seats={seats}
                isContactSales={isContactSales}
                country={country}
                isVatPayer={isVatPayer}
                locale={locale}
                t={t}
              />
            ))}
          </div>
        )}

        {/* VAT note — reflects the shared VatControls selection (incl. DPH for
            a non-payer firm, "+ DPH" net for a VAT-payer, 0 % cross-border).
            Suppressed while no price is on screen. */}
        {vatSuffix && !isContactSales && (
          <p className='-mt-6 mb-8 text-xs text-content-muted'>{vatSuffix}</p>
        )}

        {/* Volume discounts — derived from price_tiers, never written as copy. */}
        {(discountBands.length > 0 || contactThreshold !== null) && (
          <div className='mb-8'>
            <div className='flex items-center gap-2 mb-3'>
              <Users
                className='h-4 w-4 text-content-muted flex-shrink-0'
                aria-hidden='true'
              />
              <h3 className='text-sm font-semibold text-content-primary'>
                {t('orgDiscountTitle')}
              </h3>
            </div>
            <ul className='space-y-1.5 ml-6'>
              {discountBands.map((band) => (
                <li key={band.from} className='text-sm text-content-secondary'>
                  {t('orgBandRange', { from: band.from, to: band.to })}
                  {' — '}
                  {band.minPct === band.maxPct
                    ? t('orgBandDiscountExact', { pct: band.minPct })
                    : t('orgBandDiscountRange', {
                        min: band.minPct,
                        max: band.maxPct,
                      })}
                </li>
              ))}
              {contactThreshold !== null && (
                <li className='text-sm text-content-secondary'>
                  {t('orgBandOpen', { from: contactThreshold })}
                  {' — '}
                  {t('orgBandContact')}
                </li>
              )}
            </ul>
          </div>
        )}

        {/* Large-team note, shown once the entered seat count is past the last
            published band — explains why the cards stopped showing a price. */}
        {isContactSales && contactThreshold !== null && (
          <p className='-mt-4 mb-8 text-sm text-content-secondary'>
            {t('orgLargeTeamNote', { seats: contactThreshold })}
          </p>
        )}

        {/* CTA — opens the user's mail client to the sales/info address.
            ``info@lexomat.sk`` is the established contact address (ContactForm,
            how-we-rank, etc.). */}
        <Button
          asChild
          variant='outline'
          className='min-h-[48px] text-sm font-semibold border-line-strong text-brand-tertiary hover:text-brand-tertiary hover:bg-interactive-hover rounded-lg'
        >
          <a
            href={`mailto:info@lexomat.sk?subject=${encodeURIComponent(
              t('orgContactSubject')
            )}`}
          >
            {t('orgContactUs')}
            <ArrowRight className='ml-2 h-4 w-4' aria-hidden='true' />
          </a>
        </Button>
      </div>
    </section>
  );
}
